import { AltRoute } from "@mui/icons-material";
import { Chip } from "@mui/material";
import { FC, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  DirectionEnum,
  DrawerType,
  getAreaWaypointsForDirection,
  IAreaWaypoint,
  IAreaWaypointsByDirection,
  ICustomField,
  IGeoJSON,
  normalizeAreaWaypointsByDirection,
  setAreaWaypointsForDirection,
  SimpleResult,
  stampAreaIdOnAreaWaypoints,
  TAreaWaypointDirection,
  useDrawers,
} from "@ryde/shared";

import { AreaWaypointsTemplateWrapperStyled } from "./area-waypoints.styled";
import { AreaWaypointsTable } from "./area-waypoints.table";

const readAreaPolygonFromForm = (
  getValues: (name: string) => unknown,
): IGeoJSON | undefined =>
  (getValues("polygon") ?? getValues("metadata.polygon")) as
    IGeoJSON | undefined;

interface IAreaWaypointsTemplateInnerProps {
  value: IAreaWaypointsByDirection | undefined;
  polygon?: IGeoJSON;
  disabled?: boolean;
  onEdit: (params: {
    direction: TAreaWaypointDirection;
    currentValue: IAreaWaypointsByDirection;
  }) => void;
}

const AreaWaypointsTemplateInner: FC<IAreaWaypointsTemplateInnerProps> = ({
  value,
  polygon,
  disabled,
  onEdit,
}) => {
  const { t } = useTranslation();
  const areaWaypoints = useMemo(
    () => normalizeAreaWaypointsByDirection(value),
    [value],
  );

  return (
    <AreaWaypointsTemplateWrapperStyled>
      <Chip label={t("area.formLabel.areaWaypoints")} icon={<AltRoute />} />
      <div className="area-waypoints-tables">
        <AreaWaypointsTable
          direction={DirectionEnum.PickUp}
          waypoints={areaWaypoints[DirectionEnum.PickUp] ?? []}
          polygon={polygon}
          disabled={disabled}
          onEdit={direction =>
            onEdit({ direction, currentValue: areaWaypoints })
          }
        />
        <AreaWaypointsTable
          direction={DirectionEnum.DropOff}
          waypoints={areaWaypoints[DirectionEnum.DropOff] ?? []}
          polygon={polygon}
          disabled={disabled}
          onEdit={direction =>
            onEdit({ direction, currentValue: areaWaypoints })
          }
        />
      </div>
    </AreaWaypointsTemplateWrapperStyled>
  );
};

export const AreaWaypointsTemplate: FC<ICustomField> = ({
  name,
  formAttributes,
}) => {
  const { control, setValue, getValues } = useFormContext();
  const { openAsyncDrawer } = useDrawers();
  const isDisabled = Boolean(formAttributes?.disabled);
  const polygonField = useWatch({ control, name: "polygon" });
  const metadataPolygon = useWatch({ control, name: "metadata.polygon" });
  const polygon = (polygonField ?? metadataPolygon) as IGeoJSON | undefined;

  const handleEdit = useEvent(
    async ({
      direction,
      currentValue,
    }: {
      direction: TAreaWaypointDirection;
      currentValue: IAreaWaypointsByDirection;
    }) => {
      const normalizedValue = normalizeAreaWaypointsByDirection(currentValue);
      const waypoints = getAreaWaypointsForDirection({
        areaWaypoints: normalizedValue,
        direction,
      });
      const result = (await openAsyncDrawer({
        drawerProps: {
          type: DrawerType.EditAreaWaypoints,
          direction,
          waypoints,
          polygon: readAreaPolygonFromForm(getValues),
          areaName: String(getValues("name") ?? "").trim() || undefined,
          disabled: isDisabled,
        },
      })) as SimpleResult<IAreaWaypoint[]>;

      if (!result?.success || !result.data) {
        return;
      }

      const areaId = Number(getValues("id"));
      const nextValue = setAreaWaypointsForDirection({
        areaWaypoints: normalizedValue,
        direction,
        waypoints: result.data,
      });

      setValue(
        name,
        Number.isFinite(areaId) && areaId > 0
          ? stampAreaIdOnAreaWaypoints({
              areaWaypoints: nextValue,
              areaId,
            })
          : nextValue,
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    },
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value } }) => (
        <AreaWaypointsTemplateInner
          value={value}
          polygon={polygon}
          disabled={isDisabled}
          onEdit={handleEdit}
        />
      )}
    />
  );
};
